import type React from 'react';

// RBAC Types
export type UserRole = 'super_admin' | 'admin' | 'support' | 'user';
export type UserStatus = 'active' | 'disabled';

export interface RoleInfo {
  role: UserRole;
  level: number;
  displayName: string;
  description: string;
}

// Role level mapping for client-side checks
export const ROLE_LEVELS: Record<UserRole, number> = {
  super_admin: 100,
  admin: 80,
  support: 50,
  user: 10,
};

// User types
export interface User {
  id: string;
  email: string;
  displayName: string;
  handle?: string;     // 4-char handle for display on squares
  iconName?: string;   // Icon identifier from icon set
  role?: UserRole;     // User's role level (defaults to 'user')
  status?: UserStatus; // Account status (defaults to 'active')
  disabledAt?: string; // When the account was disabled
  disabledById?: string; // Who disabled the account
  disabledReason?: string; // Why the account was disabled
  tosAcceptedAt?: string; // When user accepted Terms of Service
  createdAt: string;
  updatedAt: string;
}

// Helper function to check if user is admin
export function isAdmin(user?: User | null): boolean {
  return user?.role === 'admin' || user?.role === 'super_admin';
}

// Helper function to check if user is super admin
export function isSuperAdmin(user?: User | null): boolean {
  return user?.role === 'super_admin';
}

// Helper function to check if user is support
export function isSupport(user?: User | null): boolean {
  return user?.role === 'support';
}

// Helper function to check if user has elevated access (support, admin, or super_admin)
export function hasElevatedAccess(user?: User | null): boolean {
  if (!user?.role) return false;
  return ROLE_LEVELS[user.role] >= ROLE_LEVELS.support;
}

// Helper function to check if user can manage another user
export function canManageUser(actor: User, target: User): boolean {
  if (!actor.role || !target.role) return false;
  return ROLE_LEVELS[actor.role] > ROLE_LEVELS[target.role];
}

export interface UpdateProfileInput {
  displayName?: string;
  handle?: string;
  iconName?: string;
  tosAcceptedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  expiresAt: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  displayName: string;
  referralCode?: string;
  tosAcceptedAt?: string;
}

// Board status
export type BoardStatus = 'draft' | 'live' | 'locked' | 'completed';

// Number mode determines how many number sets are used
export type NumberMode = 'single' | 'half' | 'quarter';

// Cell mode determines how squares are grouped into ownable cells
export type CellMode = 'standard' | 'paired' | 'quad';

// Cell shape determines the layout of squares within a cell
export type CellShape = '1x1' | '2x1' | '1x2' | '2x2';

// Board tier configuration for credit pricing
export interface BoardTierConfig {
  id: string;
  numberMode: NumberMode;
  creditCost: number;       // Credits required to create a board with this tier
  displayName: string;      // User-facing display name
  description: string;      // User-facing description
  updatedAt: string;
  updatedById?: string;
}

/**
 * Returns the number set keys for a board based on its sport and number mode.
 * - "single" mode: returns empty array (use primary homeNumbers/awayNumbers)
 * - "half" mode: returns ["1H", "2H"]
 * - "quarter" mode: returns sport-specific keys (Q1-Q4, P1-P3, or 1H-2H)
 */
export function getNumberSetKeys(quarters: number, mode: NumberMode): string[] {
  if (mode === 'single') return [];
  if (mode === 'half') return ['1H', '2H'];

  // For "quarter" mode, return sport-specific keys
  switch (quarters) {
    case 2:
      return ['1H', '2H'];
    case 3:
      return ['P1', 'P2', 'P3'];
    default:
      return ['Q1', 'Q2', 'Q3', 'Q4'];
  }
}

/**
 * Maps a quarter number to the appropriate number set key based on the board's number mode.
 * Returns empty string for "single" mode (use primary numbers).
 */
export function getNumberSetKeyForQuarter(
  quarter: number,
  quarters: number,
  mode: NumberMode
): string {
  if (mode === 'single') return '';

  if (mode === 'half') {
    // Map quarters to halves based on sport
    switch (quarters) {
      case 2: // Soccer - already halves
        return quarter === 1 ? '1H' : '2H';
      case 3: // Hockey - P1 is first half, P2/P3 are second half
        return quarter === 1 ? '1H' : '2H';
      default: // Football/Basketball (4 quarters)
        return quarter === 1 || quarter === 2 ? '1H' : '2H';
    }
  }

  // For "quarter" mode, return the sport-specific key
  switch (quarters) {
    case 2: // Soccer
      return quarter === 1 ? '1H' : '2H';
    case 3: // Hockey
      switch (quarter) {
        case 1:
          return 'P1';
        case 2:
          return 'P2';
        default:
          return 'P3';
      }
    default: // Football/Basketball (4 quarters)
      switch (quarter) {
        case 1:
          return 'Q1';
        case 2:
          return 'Q2';
        case 3:
          return 'Q3';
        default:
          return 'Q4';
      }
  }
}

/**
 * Gets the appropriate numbers for a given quarter based on the board's number mode.
 * Falls back to primary numbers if quarter-specific not found.
 */
export function getNumbersForQuarter(
  board: Pick<
    Board,
    'numberMode' | 'quarters' | 'homeNumbers' | 'awayNumbers' | 'quarterHomeNumbers' | 'quarterAwayNumbers'
  >,
  quarter: number
): { homeNumbers: number[]; awayNumbers: number[] } {
  if (!board.numberMode || board.numberMode === 'single') {
    return { homeNumbers: board.homeNumbers, awayNumbers: board.awayNumbers };
  }

  const key = getNumberSetKeyForQuarter(quarter, board.quarters, board.numberMode);
  const homeNumbers = board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
  const awayNumbers = board.quarterAwayNumbers?.[key] ?? board.awayNumbers;

  return { homeNumbers, awayNumbers };
}

// Type for highlighted cells with optional quarter context (for multi-number modes)
export interface HighlightedCell {
  row: number;
  col: number;
}

export interface HighlightedCellsWithQuarter {
  cells: HighlightedCell[];
  quarter?: number; // Quarter context for highlighting the correct number row
}

// Board types
export interface Board {
  id: string;
  slug?: string; // Optional URL-friendly slug (max 50 chars)
  name: string;
  description?: string; // Optional description (max 250 chars) - payment instructions, etc.
  sport: string;
  homeTeam: string;
  awayTeam: string;
  homeColor?: string;   // Optional hex color for home team (e.g., "#1976d2")
  awayColor?: string;   // Optional hex color for away team (e.g., "#d32f2f")
  gameDate: string;
  pricePerSquare: number;
  quarters: number;
  status: BoardStatus;  // draft, live, locked
  isLocked: boolean;    // Deprecated: Use status instead
  homeNumbers: number[];
  awayNumbers: number[];
  numbersRevealed: boolean;
  numberMode?: NumberMode; // "single" | "half" | "quarter" - determines how many number sets are used (defaults to "single")
  creditCostAtCreation?: number; // Credits spent to create this board (1, 2, or 4 based on tier)
  quarterHomeNumbers?: Record<string, number[]>; // Period-specific home numbers (keys: "1H", "2H", "Q1", "Q2", etc.)
  quarterAwayNumbers?: Record<string, number[]>; // Period-specific away numbers (keys: "1H", "2H", "Q1", "Q2", etc.)
  homeOnTop: boolean;
  hasBeenPublished: boolean; // True once board has ever been published (locks certain settings)
  hasBeenReset: boolean;     // True once board has been reset (prevents further resets)
  hideNames: boolean;        // Hide player names until published
  cellMode: CellMode;        // "standard" (100 cells) | "paired" (50 cells) | "quad" (25 cells)
  cellShape: CellShape;      // "1x1" | "2x1" (vertical pair) | "1x2" (horizontal pair) | "2x2" (quad)
  totalCells: number;        // 100, 50, or 25 depending on cell mode
  firstPublishedAt?: string; // Timestamp of first publish
  inviteCode: string;
  createdById: string;
  payoutConfig: PayoutSchemeConfig;
  externalGameId?: string; // Links to ExternalGame if created from live schedule
  isPremium?: boolean;     // True for boards linked to live sports API games
  themeId?: string;     // Optional theme applied to this board
  theme?: BoardTheme;   // Theme data (populated on detail view)
  managers?: BoardManager[];
  participants?: BoardParticipant[];
  openSquares?: number; // Number of unclaimed squares (only in list view)
  lastCompletedQuarter?: number | null; // Last quarter with a recorded score (only in list view)
  offlinePlayerCount?: number; // Number of offline players on the board (only in list view)
  owner?: User; // Board owner info (only in list view)
  isOwner?: boolean; // True if current user owns this board (only in list view)
  createdAt: string;
  updatedAt: string;
}

export interface CreateBoardInput {
  name: string;
  slug?: string;
  description?: string;
  sport: string;
  homeTeam: string;
  awayTeam: string;
  homeColor?: string;
  awayColor?: string;
  gameDate: string;
  pricePerSquare: number;
  quarters?: number;
  numberMode?: NumberMode; // "single" | "half" | "quarter"
  cellMode?: CellMode;     // "standard" | "paired" | "quad"
  cellShape?: CellShape;   // "1x1" | "2x1" | "1x2" | "2x2"
  payoutConfig: PayoutSchemeConfig;
  themeId?: string;
  externalGameId?: string;
}

export interface UpdateBoardInput {
  name?: string;
  description?: string;
  sport?: string;
  homeTeam?: string;
  awayTeam?: string;
  homeColor?: string;
  awayColor?: string;
  gameDate?: string;
  pricePerSquare?: number;
  quarters?: number;
  numberMode?: NumberMode;
  cellMode?: CellMode;     // "standard" | "paired" | "quad"
  cellShape?: CellShape;   // "1x1" | "2x1" | "1x2" | "2x2"
  payoutConfig?: PayoutSchemeConfig;
  homeOnTop?: boolean;
  hideNames?: boolean;
  themeId?: string;
}

export interface UpdateHitInput {
  homeScore: number;
  awayScore: number;
  description: string;
}

export interface BoardManager {
  id: string;
  boardId: string;
  userId: string;
  role: 'owner' | 'admin';
  user?: User;
  createdAt: string;
}

export interface BoardParticipant {
  id: string;
  boardId: string;
  userId: string;
  user?: User;
  joinedAt: string;
}

// Offline player types
export interface OfflinePlayer {
  id: string;
  ownerId: string;  // User who owns this offline player
  displayName: string;
  handle?: string;
  iconName?: string;
  createdAt: string;
  updatedAt: string;
  totalSquares?: number;  // Total squares across all boards (from profile API)
  boardCount?: number;    // Number of boards this player is on (from profile API)
}

// Search result for paginated offline players
export interface SearchOfflinePlayersResult {
  players: OfflinePlayer[];
  total: number;
  limit: number;
  offset: number;
}

// Offline player with board-specific data
export interface BoardOfflinePlayer extends OfflinePlayer {
  approvedSquares: number;  // Number of squares approved for this player on this board
  claimedSquares: number;   // Number of squares currently assigned to this player
  startingBalance: number;  // Initial balance for players migrated from external systems (positive = paid, negative = owes)
}

export interface CreateOfflinePlayerInput {
  displayName: string;
  handle?: string;
  iconName?: string;
  approvedSquares?: number;  // Board-specific: number of squares approved when creating via board endpoint
  startingBalance?: number;  // Board-specific: initial balance (positive = paid, negative = owes)
}

export interface UpdateOfflinePlayerInput {
  displayName?: string;
  handle?: string;
  iconName?: string;
  approvedSquares?: number;  // Board-specific: number of squares approved
}

export interface AddOfflinePlayerToBoardInput {
  approvedSquares?: number;
  startingBalance?: number;  // Initial balance (positive = paid, negative = owes)
}

export interface MergeOfflinePlayerInput {
  targetUserId: string;
  removeOfflinePlayer?: boolean;
}

export interface MergeOfflinePlayerResponse {
  message: string;
  squaresTransferred: number;
  ledgerEntriesTransferred: number;
  offlinePlayerRemoved: boolean;
}

// Merge Request types - for approval workflow
export type MergeRequestStatus = 'pending' | 'approved' | 'denied' | 'expired';

export interface MergeRequest {
  id: string;
  boardId?: string;              // NULL for parent global requests
  offlinePlayerId: string;
  targetUserId: string;
  requestedById: string;
  status: MergeRequestStatus;
  isGlobal: boolean;             // True for global (cross-board) merge requests
  globalRequestId?: string;      // Links child requests to parent global request
  removeOfflinePlayer: boolean;
  squaresToTransfer: number;
  denialReason?: string;
  reviewedAt?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  // Populated relationships
  board?: Board;
  offlinePlayer?: OfflinePlayer;
  targetUser?: User;
  requestedBy?: User;
}

export interface CreateMergeRequestInput {
  offlinePlayerId: string;
  targetUserId: string;
  removeOfflinePlayer?: boolean;
}

export interface CreateGlobalMergeRequestInput {
  targetUserId: string;
  removeOfflinePlayer?: boolean;
}

export interface DenyMergeRequestInput {
  reason?: string;
}

// Global merge request types
export interface BoardPresenceInfo {
  boardId: string;
  boardName: string;
  squareCount: number;
  ledgerEntries: number;
  isManager: boolean;
  targetUserIsMember?: boolean; // Set when checking for a specific target user
}

export interface BoardMergeSummary {
  boardId: string;
  boardName: string;
  squareCount: number;
  ledgerEntries: number;
}

export interface GlobalMergeRequestResult {
  globalRequestId: string;
  requests: MergeRequest[];
  totalSquares: number;
  boardSummaries: BoardMergeSummary[];
}

export interface GlobalMergeResult {
  totalSquaresTransferred: number;
  totalLedgerEntries: number;
  boardResults: BoardMergeResult[];
}

export interface BoardMergeResult {
  boardId: string;
  boardName: string;
  squaresTransferred: number;
  ledgerEntries: number;
}

// Global merge request details (for user approval UI)
export interface GlobalMergeRequestDetails {
  globalRequestId: string;
  offlinePlayer?: OfflinePlayer;
  requestedBy?: User;
  targetUser?: User;
  totalSquares: number;
  totalLedgerAmount: number;
  expiresAt: string;
  createdAt: string;
  boardDetails: GlobalMergeRequestBoard[];
}

export interface GlobalMergeRequestBoard {
  boardId: string;
  boardName: string;
  squareCount: number;
  ledgerEntries: number;
}

// Player Connection types - for tracking frequently-used players
export interface PlayerConnection {
  id: string;
  ownerId: string;
  connectedUserId?: string;
  connectedUser?: User;
  offlinePlayerId?: string;
  offlinePlayer?: OfflinePlayer;
  nickname?: string;
  notes?: string;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlayerConnectionWithStats extends PlayerConnection {
  sharedBoardCount: number;
  lastBoardName?: string;
  lastBoardId?: string;
}

export interface CreateConnectionInput {
  connectedUserId?: string;
  offlinePlayerId?: string;
  nickname?: string;
  notes?: string;
}

export interface UpdateConnectionInput {
  nickname?: string;
  notes?: string;
}

// Board Invite types
export interface BoardInvite {
  id: string;
  boardId: string;
  invitedEmail: string;
  invitedById: string;
  invitedByName?: string;
  message?: string;
  sentAt: string;
  lastSentAt: string;
  sendCount: number;
  acceptedAt?: string;
  acceptedById?: string;
  acceptedByName?: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'expired';
}

export interface InviteParticipantResponse {
  message: string;
  inviteId: string;
  isResend: boolean;
  sendCount: number;
  rateLimitRemaining: number;
}

export interface ResendInviteResponse {
  message: string;
  sendCount: number;
  rateLimitRemaining: number;
}

// Square types
export interface Square {
  id: string;
  boardId: string;
  row: number;
  col: number;
  ownerId?: string;
  owner?: User;
  offlinePlayerId?: string;
  offlinePlayer?: OfflinePlayer;
  label?: string;
  claimedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SquareWithNumbers extends Square {
  homeNumber: number;
  awayNumber: number;
  cellNumber: number; // Cell number (1-indexed) for cell grouping modes
}

export interface ClaimSquareInput {
  row: number;
  col: number;
}

export interface AssignSquareInput {
  row: number;
  col: number;
  userId?: string;
  offlinePlayerId?: string;
}

export interface LabelSquareInput {
  row: number;
  col: number;
  label: string;
}

// Hit types
export interface Hit {
  id: string;
  boardId: string;
  quarter?: number | null;   // null for custom hits, 1-4/0 for period hits
  categoryId?: string | null; // null for period hits, set for custom hits
  homeScore: number;
  awayScore: number;
  winningRow: number;
  winningCol: number;
  winningSquareId?: string;
  winningSquare?: Square;
  payoutAmount: number;
  description: string;
  isWrongWay?: boolean;       // True if this is the wrong-way (reverse) winner hit
  isPlusN?: boolean;          // True if this is the +N rule winner hit
  parentHitId?: string | null; // Links wrong-way/+N hit to its main hit
  recordedById: string;
  createdAt: string;
  neighborPayouts?: NeighborPayoutEntry[]; // Neighbor payouts associated with this hit
}

// Neighbor payout entry returned with hits
export interface NeighborPayoutEntry {
  row: number;
  col: number;
  direction: string;
  amount: number;
  user?: User;
  offlinePlayer?: OfflinePlayer;
}

export interface RecordHitInput {
  quarter: number;
  homeScore: number;
  awayScore: number;
  description: string;
}

// Custom hit input for stat-based payouts (e.g., Passing Yards, Rush Yards)
export interface RecordCustomHitInput {
  categoryId: string;  // ID of the custom payout category
  homeValue: number;   // e.g., home team passing yards
  awayValue: number;   // e.g., away team passing yards
  description?: string; // optional description
}

export interface HitResult {
  hit: Hit;
  winningSquare: Square;
  mainWinner?: WinnerPayout;
  reverseWinner?: WinnerPayout;   // Legacy: ephemeral reverse payout
  wrongWayHit?: Hit;              // New: actual wrong-way hit record
  wrongWayWinner?: WinnerPayout;  // New: wrong-way winner info
  neighborWinners?: WinnerPayout[];
  plusNHit?: Hit;                 // Actual +N rule hit record
  plusNWinner?: WinnerPayout;     // +N rule winner info
  plusFiveWinner?: WinnerPayout;  // Legacy: ephemeral +5 payout (deprecated)
}

export interface WinnerPayout {
  square: Square;
  user?: User;
  amount: number;
  type: 'main' | 'reverse' | 'neighbor' | 'plus_five' | 'plus_n' | 'wrong_way';
}

// Ledger types
export interface LedgerEntry {
  id: string;
  boardId: string;
  userId?: string;
  user?: User;
  offlinePlayerId?: string;
  offlinePlayer?: OfflinePlayer;
  amount: number;
  entryType: 'square_purchase' | 'payout' | 'manual_payment' | 'bad_numbers';
  description: string;
  hitId?: string;
  createdById: string;
  createdAt: string;
}

export interface UserBalance {
  userId: string;
  user?: User;
  offlinePlayerId?: string;
  offlinePlayer?: OfflinePlayer;
  isOfflinePlayer: boolean;
  approvedSquares: number;     // Number of approved squares
  claimedSquares: number;      // Number of squares actually claimed
  totalOwed: number;           // Amount user owes (based on claimed squares)
  totalEarned: number;         // Amount user has won from scores
  totalPaid: number;           // Payments user has made toward their squares
  winningsPaidOut: number;     // Amount of winnings that have been paid out
  hasPaid: boolean;            // Whether user has fully paid for their squares
  winningsFullyPaid: boolean;  // Whether all winnings have been paid out
  netBalance: number;          // Final balance (positive = user is owed, negative = user owes)
}

export interface LedgerSummary {
  totalPot: number;
  totalPaidOut: number;
  balances: UserBalance[];
}

export interface ManualPaymentInput {
  userId?: string;
  offlinePlayerId?: string;
  amount: number;
  description: string;
}

export interface MarkWinningsPaidInput {
  userId?: string;
  offlinePlayerId?: string;
  paid: boolean;
}

// Custom payout category for stat-based payouts (e.g., Passing Yards, Rush Yards)
export interface CustomPayoutCategory {
  id: string;           // UUID or slug identifier
  name: string;         // e.g., "Passing Yards"
  payoutAmount: number; // Fixed $ amount (e.g., 25.00)
  displayOrder: number; // Order in which to display
  wrongWayEnabled?: boolean; // If true, auto-pay both directions (doubles payout)
}

// Payout configuration types
export interface PayoutSchemeConfig {
  scheme: 'standard' | 'reverse' | 'neighbors' | 'per_quarter';
  totalPot: number;
  profitPercent?: number; // Organizer profit percentage (0-50%)
  rightWayEnabled?: boolean; // Enable Right Way (main) payouts per quarter (default true)
  quarterPayouts: Record<string, number>; // Dollar amounts for each quarter, e.g., {"Q1": 100, "Q2": 200, "Q3": 150, "Q4": 550}
  wrongWayEnabled?: boolean; // Enable Wrong Way payouts per quarter
  quarterWrongWayPayouts?: Record<string, number>; // Per-quarter wrong-way payouts, e.g., {"Q1": 10, "Q2": 15}
  quarterPlusNPayouts?: Record<string, number>; // Per-quarter +N rule payouts for Right Way, e.g., {"Q1": 5, "Q2": 10}
  quarterPlusNWrongWayPayouts?: Record<string, number>; // Per-quarter +N rule payouts for Wrong Way, e.g., {"Q1": 5, "Q2": 10}
  plusNRightWayEnabled?: boolean; // Enable +N rule for Right Way matches
  plusNWrongWayEnabled?: boolean; // Enable +N rule for Wrong Way matches
  customCategories?: CustomPayoutCategory[]; // Custom stat-based payout categories
  neighborPayouts?: NeighborConfig;
  boxNeighborPayouts?: BoxNeighborConfig; // Box neighbor payouts (mutually exclusive with neighborPayouts)
  reverseEnabled: boolean;  // Legacy: board-level reverse (deprecated, use quarterWrongWayPayouts)
  reverseAmount?: number;   // Legacy: $ amount for reverse (deprecated)
  plusFiveEnabled: boolean;
  plusFiveAmount?: number; // Legacy: flat $ amount for +N (deprecated, use quarterPlusNPayouts)
  plusNOffset?: number;    // The offset value for the +N rule (default 5, can be any number like 3, 5, 7)
  overtimeConfig?: OvertimeConfig;
  badNumbersConfig?: BadNumbersConfig; // Bad numbers automatic payout configuration
}

export interface OvertimeConfig {
  enabled: boolean;
  overtimeSplit?: number; // Legacy: % of Q4's allocation that goes to OT (deprecated, use overtimeAmount)
  overtimeAmount?: number; // Dollar amount for Final/OT payout when overtime occurs
}

// Bad numbers configuration - pays automatic fixed amounts for "unlucky" digit combinations
// when the board is locked, before the game even starts
export interface BadNumbersConfig {
  enabled: boolean;
  badCombinations?: string[];  // Specific row-col combinations like ["2-5", "2-2", "8-9"]
  payoutAmount: number;        // Fixed dollar amount per bad square
  // DEPRECATED: badDigits is kept for backward compatibility - use badCombinations instead
  badDigits?: number[];        // Legacy: digits considered "bad" (e.g., [2, 5, 8, 9])
}

// Neighbor direction types for directional control
export type NeighborDirection = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

// All 8 compass directions
export const ALL_NEIGHBOR_DIRECTIONS: NeighborDirection[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

// Cardinal directions only (N, S, E, W)
export const CARDINAL_DIRECTIONS: NeighborDirection[] = ['N', 'E', 'S', 'W'];

// Diagonal directions only (NE, SE, SW, NW)
export const DIAGONAL_DIRECTIONS: NeighborDirection[] = ['NE', 'SE', 'SW', 'NW'];

// Direction display names for UI
export const NEIGHBOR_DIRECTION_LABELS: Record<NeighborDirection, string> = {
  N: 'North',
  NE: 'Northeast',
  E: 'East',
  SE: 'Southeast',
  S: 'South',
  SW: 'Southwest',
  W: 'West',
  NW: 'Northwest',
};

// Direction offsets for calculating neighbor positions (row, col)
export const NEIGHBOR_DIRECTION_OFFSETS: Record<NeighborDirection, [number, number]> = {
  N: [-1, 0],
  NE: [-1, 1],
  E: [0, 1],
  SE: [1, 1],
  S: [1, 0],
  SW: [1, -1],
  W: [0, -1],
  NW: [-1, -1],
};

// Helper function to calculate neighbor position with wrap-around (toroidal grid)
export function getNeighborPosition(row: number, col: number, direction: NeighborDirection): { row: number; col: number } {
  const [dr, dc] = NEIGHBOR_DIRECTION_OFFSETS[direction];
  return {
    row: (row + dr + 10) % 10,
    col: (col + dc + 10) % 10,
  };
}

// Helper function to get all neighbor positions for given directions
export function getNeighborPositions(
  row: number,
  col: number,
  directions: NeighborDirection[]
): Array<{ row: number; col: number; direction: NeighborDirection }> {
  return directions.map(direction => ({
    ...getNeighborPosition(row, col, direction),
    direction,
  }));
}

export interface NeighborConfig {
  enabled: boolean;
  directions?: NeighborDirection[];        // Which directions pay out: ["N", "S", "E", "W", "NE", "SE", "SW", "NW"]
  usePerQuarterAmounts?: boolean;           // If true, use quarterAmounts; otherwise use globalAmount
  globalAmount?: number;                    // Fixed dollar amount for all quarters/directions
  quarterAmounts?: Record<string, number>;  // Per-quarter amounts: {"Q1": 20, "Q2": 25, "Q3": 25, "Q4": 30}
  // Legacy fields for backward compatibility
  payoutPercent?: number;                   // Deprecated: use globalAmount or quarterAmounts instead
  type?: 'adjacent' | 'box' | 'nearest';    // Deprecated: use directions instead
}

// =====================
// Box Neighbor Types
// =====================

export type BoxNeighborSize = '2x2' | '5x2' | '5x5';

export interface BoxNeighborConfig {
  enabled: boolean;
  boxSize: BoxNeighborSize;
  usePerQuarterAmounts?: boolean;           // If true, use quarterAmounts; otherwise use globalAmount
  globalAmount?: number;                    // Amount PER neighbor (not split among neighbors)
  quarterAmounts?: Record<string, number>;  // Per-quarter amount PER neighbor
}

export const BOX_NEIGHBOR_SIZES: { value: BoxNeighborSize; label: string; neighborCount: number; description: string }[] = [
  { value: '2x2', label: '2x2 Boxes', neighborCount: 3, description: '25 groups of 4 squares' },
  { value: '5x2', label: '5x2 Boxes', neighborCount: 9, description: '10 groups of 10 squares' },
  { value: '5x5', label: '5x5 Quadrants', neighborCount: 24, description: '4 quadrants of 25 squares' },
];

/**
 * Get the box group index for a position on the grid.
 * @param row Row index (0-9)
 * @param col Column index (0-9)
 * @param boxSize Size of box groups ('2x2', '5x2', or '5x5')
 * @returns Box group index (0-24 for 2x2, 0-9 for 5x2, 0-3 for 5x5)
 */
export function getBoxGroupForPosition(row: number, col: number, boxSize: BoxNeighborSize): number {
  if (boxSize === '2x2') {
    const boxRow = Math.floor(row / 2);
    const boxCol = Math.floor(col / 2);
    return boxRow * 5 + boxCol;
  } else if (boxSize === '5x5') {
    // 5x5: 4 quadrants (top-left, top-right, bottom-left, bottom-right)
    const boxRow = Math.floor(row / 5);
    const boxCol = Math.floor(col / 5);
    return boxRow * 2 + boxCol;
  } else {
    // 5x2: 5 columns wide, 2 rows tall
    const boxRow = Math.floor(row / 2);
    const boxCol = Math.floor(col / 5);
    return boxRow * 2 + boxCol;
  }
}

/**
 * Get all positions in the same box group as the given position.
 * @param row Row index (0-9)
 * @param col Column index (0-9)
 * @param boxSize Size of box groups ('2x2', '5x2', or '5x5')
 * @returns Array of all positions in the same box (including the input position)
 */
export function getBoxPositions(row: number, col: number, boxSize: BoxNeighborSize): Array<{ row: number; col: number }> {
  if (boxSize === '2x2') {
    const boxRowStart = Math.floor(row / 2) * 2;
    const boxColStart = Math.floor(col / 2) * 2;
    return [
      { row: boxRowStart, col: boxColStart },
      { row: boxRowStart, col: boxColStart + 1 },
      { row: boxRowStart + 1, col: boxColStart },
      { row: boxRowStart + 1, col: boxColStart + 1 },
    ];
  } else if (boxSize === '5x5') {
    // 5x5: 4 quadrants of 25 squares
    const boxRowStart = Math.floor(row / 5) * 5;
    const boxColStart = Math.floor(col / 5) * 5;
    const positions: Array<{ row: number; col: number }> = [];
    for (let r = boxRowStart; r < boxRowStart + 5; r++) {
      for (let c = boxColStart; c < boxColStart + 5; c++) {
        positions.push({ row: r, col: c });
      }
    }
    return positions;
  } else {
    // 5x2: 5 columns wide, 2 rows tall
    const boxRowStart = Math.floor(row / 2) * 2;
    const boxColStart = Math.floor(col / 5) * 5;
    const positions: Array<{ row: number; col: number }> = [];
    for (let r = boxRowStart; r < boxRowStart + 2; r++) {
      for (let c = boxColStart; c < boxColStart + 5; c++) {
        positions.push({ row: r, col: c });
      }
    }
    return positions;
  }
}

/**
 * Get box neighbor positions for a given position (excludes the input position itself).
 * @param row Row index (0-9)
 * @param col Column index (0-9)
 * @param boxSize Size of box groups ('2x2', '5x2', or '5x5')
 * @returns Array of neighbor positions in the same box
 */
export function getBoxNeighborPositions(row: number, col: number, boxSize: BoxNeighborSize): Array<{ row: number; col: number }> {
  return getBoxPositions(row, col, boxSize).filter(pos => pos.row !== row || pos.col !== col);
}

/**
 * Get the number of neighbors in a box group (excludes the winner).
 * @param boxSize Size of box groups ('2x2', '5x2', or '5x5')
 * @returns Number of neighbors (3 for 2x2, 9 for 5x2, 24 for 5x5)
 */
export function getBoxNeighborCount(boxSize: BoxNeighborSize): number {
  switch (boxSize) {
    case '2x2': return 3;   // 4 squares - 1 winner = 3 neighbors
    case '5x2': return 9;   // 10 squares - 1 winner = 9 neighbors
    case '5x5': return 24;  // 25 squares - 1 winner = 24 neighbors
    default: return 9;      // Default to 5x2
  }
}

// Sports
export type Sport = 'football' | 'basketball' | 'hockey' | 'soccer' | 'golf' | 'other';

export const SPORTS: { value: Sport; label: string }[] = [
  { value: 'football', label: 'Football' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'hockey', label: 'Hockey' },
  { value: 'soccer', label: 'Soccer' },
  { value: 'golf', label: 'Golf' },
  { value: 'other', label: 'Other' },
];

// =====================
// Live Sports API Types
// =====================

export type GameStatus = 'scheduled' | 'in_progress' | 'final' | 'postponed' | 'canceled';

export interface SportConfig {
  id: string;
  sportKey: string;
  displayName: string;
  enabled: boolean;
  apiProvider: string;
  seasonYear: number;
  quarters: number;
  liveBoardCreditCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalGame {
  id: string;
  sportKey: string;
  externalGameId: string;
  homeTeam: string;
  awayTeam: string;
  homeTeamId: string;
  awayTeamId: string;
  gameDate: string;
  status: GameStatus;
  homeScore: number | null;
  awayScore: number | null;
  quarter: number | null;
  quarterStr: string | null;
  timeRemaining: string;
  venue: string;
  season: number;
  week: number | null;
  lastSyncedAt: string;
  createdAt: string;
  updatedAt: string;
}

export type ScoreUpdateStatus = 'pending' | 'accepted' | 'dismissed' | 'edited';

export interface PendingScoreUpdate {
  id: string;
  boardId: string;
  externalGameId: string;
  quarter: number;
  homeScore: number;
  awayScore: number;
  status: ScoreUpdateStatus;
  reviewedById?: string;
  reviewedAt?: string;
  hitId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMapping {
  id: string;
  sportKey: string;
  externalTeamId: string;
  teamName: string;
  abbreviation: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  createdAt: string;
  updatedAt: string;
}

// =====================
// Theme Types
// =====================

// Theme category for organization
export type ThemeCategory = 'classic' | 'sport' | 'nfl' | 'nba' | 'college' | 'holiday' | 'custom';

export const THEME_CATEGORIES: { value: ThemeCategory; label: string; description: string }[] = [
  { value: 'classic', label: 'Classic', description: 'Traditional squares board styles' },
  { value: 'sport', label: 'Sports', description: 'Generic sport-themed designs' },
  { value: 'nfl', label: 'NFL Teams', description: 'Official NFL team themes' },
  { value: 'nba', label: 'NBA Teams', description: 'Official NBA team themes' },
  { value: 'college', label: 'College', description: 'College football themes' },
  { value: 'holiday', label: 'Holiday', description: 'Seasonal and holiday themes' },
  { value: 'custom', label: 'Custom', description: 'Special and custom designs' },
];

// Theme decoration element
export interface ThemeDecoration {
  type: 'image' | 'emoji' | 'shape';
  content: string; // URL for image, emoji char, or shape type
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right';
  size?: string; // CSS size e.g., "80px", "10%"
  opacity?: number; // 0-1, default 1
  offsetX?: string; // CSS offset from position
  offsetY?: string; // CSS offset from position
}

// Complete theme styling data (matches backend ThemeData)
export interface ThemeData {
  // Grid & Cell styling
  gridBackground: string;
  cellBackground: string;
  cellBorder: string;
  cellBorderWidth: number;
  cellBorderRadius: number;

  // Square state colors
  squareUnclaimed: string;
  squareClaimedOther: string;
  squareClaimedSelf: string;
  squareWinner: string;
  squareSelected: string;
  squareOfflinePlayer: string;

  // Team colors
  homeTeamColor: string;
  homeTeamAccent: string;
  awayTeamColor: string;
  awayTeamAccent: string;

  // Header styling
  headerBackground: string;
  headerTextColor: string;
  headerFontWeight: string;

  // Typography
  fontFamily: string;
  cellFontSize: string;
  cellFontWeight: string;

  // Winner badge
  winnerBadgeBackground: string;
  winnerBadgeTextColor: string;

  // Legend
  legendStyle: string;

  // Page/Container Styling
  pageBackground?: string; // Background color for page behind board
  pageGradient?: string; // CSS gradient for page background
  backgroundImage?: string; // URL to background image
  backgroundOverlay?: string; // Overlay color with opacity (e.g., "rgba(0,0,0,0.3)")

  // Decorative Elements - rendered on live view and export
  decorations?: ThemeDecoration[];

  // Animation Effects (live view only, not exported)
  animation?: 'snow' | 'confetti' | 'fireworks' | 'none';

  // Export Text Color Mode - controls text visibility on themed backgrounds
  textColorMode?: 'light' | 'dark'; // "light" for dark backgrounds, "dark" for light backgrounds

  // Export Info Panel Background - background color for left side info panel in exports
  infoPanelBackground?: string; // e.g., "rgba(0,0,0,0.35)" for dark overlay on light text themes

  // Optional branding
  logoUrl?: string;
  backgroundPattern?: string;

  // Box Neighbor styling
  boxNeighborBorderColor?: string; // Border color for box neighbor groups (e.g., "#1976d2")
  boxNeighborBorderWidth?: number; // Border width in pixels (default 3)

  // Cell Group styling (for paired/quad modes)
  cellGroupBorderColor?: string; // Border color for cell groups (e.g., "rgba(0, 0, 0, 0.5)")
  cellGroupBorderWidth?: number; // Border width in pixels (default 3)
}

// Board theme record
export interface BoardTheme {
  id: string;
  lookupKey: string;
  name: string;
  description?: string;
  category: ThemeCategory;
  previewImage?: string;
  themeData: ThemeData;
  creditPrice: number; // Price in credits (0 = free)
  productId?: string; // Deprecated
  isFree: boolean;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Theme with ownership status (for store display)
export interface ThemeWithOwnership extends BoardTheme {
  owned: boolean; // Backend returns 'owned', not 'isOwned'
  acquiredAt?: string;
}

// Input types for theme operations
export interface CreateThemeInput {
  lookupKey: string;
  name: string;
  description?: string;
  category: ThemeCategory;
  previewImage?: string;
  themeData: ThemeData;
  creditPrice?: number;
  isFree?: boolean;
  displayOrder?: number;
  active?: boolean;
}

export interface UpdateThemeInput {
  name?: string;
  description?: string;
  category?: ThemeCategory;
  previewImage?: string;
  themeData?: ThemeData;
  creditPrice?: number;
  isFree?: boolean;
  displayOrder?: number;
  active?: boolean;
}

// Theme usage statistics (admin)
export interface ThemeUsageStats {
  themeId: string;
  themeName: string;
  totalOwners: number;
  totalBoardsUsing: number;
  totalRevenue: number;
}

// Default theme data for Classic theme
export const DEFAULT_THEME_DATA: ThemeData = {
  gridBackground: '#ffffff',
  cellBackground: '#ffffff',
  cellBorder: '#e0e0e0',
  cellBorderWidth: 1,
  cellBorderRadius: 0,
  squareUnclaimed: '#f5f5f5',
  squareClaimedOther: '#e3f2fd',
  squareClaimedSelf: '#c8e6c9',
  squareWinner: '#fff9c4',
  squareSelected: '#bbdefb',
  squareOfflinePlayer: '#f3e5f5',
  homeTeamColor: '#1976d2',
  homeTeamAccent: '#1565c0',
  awayTeamColor: '#d32f2f',
  awayTeamAccent: '#c62828',
  headerBackground: '#fafafa',
  headerTextColor: '#212121',
  headerFontWeight: '600',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  cellFontSize: '0.75rem',
  cellFontWeight: '500',
  winnerBadgeBackground: '#ffd700',
  winnerBadgeTextColor: '#000000',
  legendStyle: 'default',
};

// Default payout percentages (used for profile defaults and converting to dollar amounts)
export const DEFAULT_PAYOUT_PERCENTAGES: Record<string, number> = {
  Q1: 0.1,
  Q2: 0.1,
  Q3: 0.1,
  Q4: 0.7,
};

// Default payout configurations (quarterPayouts will be calculated as dollars based on price)
export const DEFAULT_PAYOUT_CONFIG: PayoutSchemeConfig = {
  scheme: 'standard',
  totalPot: 0,
  rightWayEnabled: true, // Right Way payouts enabled by default
  quarterPayouts: {}, // Dollar amounts will be calculated when board is created
  wrongWayEnabled: false,
  reverseEnabled: false,
  plusFiveEnabled: false,
};

export const QUARTER_LABELS: Record<number, string> = {
  1: 'Q1',
  2: 'Q2 / Halftime',
  3: 'Q3',
  4: 'Q4 / Final',
  0: 'Final',
};

// Square request types
export interface SquareRequest {
  id: string;
  boardId: string;
  userId: string;
  user?: User;
  requestedSquares: number;
  approvedSquares?: number;
  status: 'pending' | 'approved' | 'denied';
  denialReason?: string;
  reviewedById?: string;
  reviewedBy?: User;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSquareRequestInput {
  requestedSquares: number;
}

export interface CreateSquareRequestResponse {
  request: SquareRequest;
  remainingActiveRequests: number;
  maxActiveRequests: number;
}

export interface ApproveSquareRequestInput {
  approvedSquares?: number;
}

export interface DenySquareRequestInput {
  reason?: string;
}

export interface AssignSquaresInput {
  userId: string;
  squares: number;
  startingBalance?: number; // Optional starting balance for migrating external payments
}

// Audit log types
export interface AuditLog {
  id: string;
  boardId: string;
  userId: string;
  user?: User;
  action: string;
  category: string;
  description: string;
  details: AuditDetails;
  ipAddress?: string;
  createdAt: string;
}

export interface AuditDetails {
  squareRow?: number;
  squareCol?: number;
  targetUserId?: string;
  targetUserName?: string;
  oldValue?: string;
  newValue?: string;
  quarter?: number;
  categoryId?: string; // For custom payout category hits
  homeScore?: number;
  awayScore?: number;
  amount?: number;
  extraInfo?: Record<string, unknown>;
}

export interface AuditLogResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditFilters {
  categories: string[];
  actions: string[];
}

// Audit action constants for display
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  board_created: 'Board Created',
  board_updated: 'Board Updated',
  board_published: 'Board Published',
  board_unpublished: 'Board Unpublished',
  board_locked: 'Board Locked',
  board_unlocked: 'Board Unlocked',
  board_completed: 'Board Completed',
  board_reset: 'Board Reset',
  description_updated: 'Description Updated',
  numbers_randomized: 'Numbers Randomized',
  payout_config_updated: 'Payout Config Updated',
  players_randomized: 'Players Randomized',
  team_name_updated: 'Team Name Updated',
  manager_added: 'Manager Added',
  player_invited: 'Player Invited',
  player_joined: 'Player Joined',
  player_removed: 'Player Removed',
  square_request_approved: 'Request Approved',
  square_request_denied: 'Request Denied',
  square_requested: 'Squares Requested',
  square_assigned: 'Square Assigned',
  square_claimed: 'Square Claimed',
  square_cleared: 'Square Cleared',
  score_deleted: 'Score Deleted',
  score_recorded: 'Score Recorded',
  score_updated: 'Score Updated',
  payment_recorded: 'Payment Recorded',
  payment_updated: 'Payment Updated',
  payout_issued: 'Payout Issued',
  winnings_paid_out: 'Winnings Paid Out',
  merge_request_created: 'Merge Request Created',
  merge_request_approved: 'Merge Request Approved',
  merge_request_denied: 'Merge Request Denied',
  merge_request_cancelled: 'Merge Request Cancelled',
  offline_player_merged: 'Offline Player Merged',
};

export const AUDIT_CATEGORY_LABELS: Record<string, string> = {
  board: 'Board',
  players: 'Players',
  squares: 'Squares',
  hits: 'Scores',
  payments: 'Payments',
};

// =====================
// Admin Types
// =====================

// User with computed statistics for admin view
export interface UserWithStats extends User {
  boardCount: number;
  squareCount: number;
  totalSpent: number;
  totalEarned: number;
}

// Paginated list result
export interface PaginatedResult {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Admin user list result
export interface ListUsersResult extends PaginatedResult {
  users: UserWithStats[];
}

// Admin user list filters
export interface ListUsersInput {
  search?: string;
  role?: UserRole;
  status?: UserStatus;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

// Dashboard statistics
export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  disabledUsers: number;
  adminCount: number;
  totalBoards: number;
  liveBoards: number;
  completedBoards: number;
  totalSquares: number;
  claimedSquares: number;
  totalRevenue: number;
  totalPayouts: number;
}

// System audit action types
export type SystemAuditAction =
  | 'role_change'
  | 'user_disabled'
  | 'user_enabled'
  | 'user_created'
  | 'user_deleted'
  | 'impersonation'
  | 'admin_login'
  | 'board_reassign'
  | 'payment_review'
  | 'system_config';

// System audit log entry
export interface SystemAuditLog {
  id: string;
  actorId: string;
  actor?: User;
  action: SystemAuditAction;
  targetUserId?: string;
  targetUser?: User;
  targetBoardId?: string;
  description: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

// System audit log list result
export interface ListAuditLogsResult extends PaginatedResult {
  logs: SystemAuditLog[];
}

// Board with owner info for admin view
export interface BoardWithOwner extends Board {
  owner: User;
  ownerDisabled: boolean;
  squaresClaimed: number;
  totalPot: number;
}

// Admin board list result
export interface ListBoardsResult extends PaginatedResult {
  boards: BoardWithOwner[];
}

// Admin board list filters
export interface ListBoardsInput {
  search?: string;
  status?: BoardStatus;
  includeDisabled?: boolean;
  page?: number;
  pageSize?: number;
}

// Payment with context for admin view
export interface PaymentWithContext extends LedgerEntry {
  boardName: string;
  userName: string;
}

// Admin payments list result
export interface ListPaymentsResult extends PaginatedResult {
  payments: PaymentWithContext[];
}

// Admin payments list filters
export interface ListPaymentsInput {
  boardId?: string;
  userId?: string;
  entryType?: string;
  page?: number;
  pageSize?: number;
}

// System audit action labels
export const SYSTEM_AUDIT_ACTION_LABELS: Record<SystemAuditAction, string> = {
  role_change: 'Role Changed',
  user_disabled: 'User Disabled',
  user_enabled: 'User Enabled',
  user_created: 'User Created',
  user_deleted: 'User Deleted',
  impersonation: 'User Impersonation',
  admin_login: 'Admin Login',
  board_reassign: 'Board Reassigned',
  payment_review: 'Payment Reviewed',
  system_config: 'System Configuration',
};

// Role display names
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  support: 'Support',
  user: 'User',
};

// Status display names
export const STATUS_DISPLAY_NAMES: Record<UserStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
};

// =====================
// Accounting Types
// =====================

// Aggregated user balance across multiple completed boards
export interface AggregatedUserBalance {
  userId?: string;
  offlinePlayerId?: string;
  user?: User;
  offlinePlayer?: OfflinePlayer;
  isOfflinePlayer: boolean;
  totalOwed: number;       // Sum of what user owes across boards
  totalPaid: number;       // Sum of payments made
  totalEarned: number;     // Sum of winnings
  winningsPaidOut: number; // Sum of winnings paid out
  netBalance: number;      // Overall net balance
  boardCount: number;      // Number of boards participated
  fullySettled: boolean;   // All boards settled
}

// Cash flow statistics for a time period
export interface CashFlowStats {
  paymentsReceived: number;  // Money received from players
  winningsPaidOut: number;   // Money paid out to winners
  netCashFlow: number;       // Net cash flow (received - paid out)
}

// Overall accounting summary for a manager
export interface AccountingSummary {
  balances: AggregatedUserBalance[];
  totalOwedToManager: number;   // Sum of all negative balances (money to collect)
  totalOwedByManager: number;   // Sum of all positive balances (money to pay out)
  completedBoardsCount: number; // Number of completed boards managed
  cashFlow7Days: CashFlowStats;    // Cash flow for last 7 days
  cashFlow30Days: CashFlowStats;   // Cash flow for last 30 days
  cashFlowThisYear: CashFlowStats; // Cash flow for current year
  cashFlowAllTime: CashFlowStats;  // Cash flow for all time
}

// Accounting detail for a user on a specific board
export interface BoardAccountingDetail {
  board: Board;
  approvedSquares: number;
  claimedSquares: number;
  totalOwed: number;
  totalPaid: number;
  totalEarned: number;
  winningsPaidOut: number;
  netBalance: number;
  hasPaid: boolean;
  winningsFullyPaid: boolean;
  ledgerEntries: LedgerEntry[]; // Full transaction history
}

// Complete accounting detail for a user across all boards
export interface UserAccountingDetail {
  userId?: string;
  offlinePlayerId?: string;
  user?: User;
  offlinePlayer?: OfflinePlayer;
  isOfflinePlayer: boolean;
  boards: BoardAccountingDetail[];
  totalOwed: number;
  totalPaid: number;
  totalEarned: number;
  winningsPaidOut: number;
  netBalance: number;
}

// =====================
// Player Accounting Types (self-view)
// =====================

// Player's balance with a specific manager
export interface ManagerBalance {
  managerId: string;
  manager?: User;
  totalOwed: number;       // What player owes to this manager
  totalPaid: number;       // What player has paid to this manager
  totalEarned: number;     // What player has won from this manager's boards
  winningsPaidOut: number; // Winnings paid out by this manager
  netBalance: number;      // Balance with this manager
  boardCount: number;      // Boards with this manager
  fullySettled: boolean;   // Settled with this manager
}

// Player's view of their balances across all managers
export interface PlayerAccountingSummary {
  managerBalances: ManagerBalance[];
  totalOwedToManagers: number;  // Sum of negative balances (money player owes)
  totalOwedByManagers: number;  // Sum of positive balances (money owed to player)
  completedBoardsCount: number; // Total completed boards played
}

// Player's detailed view with a specific manager
export interface PlayerManagerDetail {
  managerId: string;
  manager?: User;
  boards: BoardAccountingDetail[];
  totalOwed: number;
  totalPaid: number;
  totalEarned: number;
  winningsPaidOut: number;
  netBalance: number;
}

// =====================
// Board Template Types
// =====================

// Template square assignment - position-based assignment of offline players or player connections
export interface TemplateSquareAssignment {
  id: string;
  templateId: string;
  offlinePlayerId?: string;  // Optional - for direct offline player assignments
  offlinePlayer?: OfflinePlayer;
  playerConnectionId?: string;  // Optional - for connection-based assignments
  playerConnection?: PlayerConnection;
  row: number;
  col: number;
  createdAt: string;
}

// Board template - reusable board configuration with optional player assignments
export interface BoardTemplate {
  id: string;
  ownerId: string;
  name: string;
  description?: string;
  sport: string;
  quarters: number;
  pricePerSquare: number;
  numberMode?: NumberMode; // "single" | "half" | "quarter" (defaults to "single")
  cellMode?: CellMode;     // "standard" | "paired" | "quad" (defaults to "standard")
  cellShape?: CellShape;   // "1x1" | "2x1" | "1x2" | "2x2" (defaults to "1x1")
  payoutConfig: PayoutSchemeConfig;
  themeId?: string; // Default theme to apply when creating boards from this template
  squareAssignments?: TemplateSquareAssignment[];
  // assignedSquaresCount is returned by the list endpoint (not full assignments array)
  assignedSquaresCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Input for creating a new template
export interface CreateTemplateInput {
  name: string;
  description?: string;
  sport: string;
  quarters?: number;
  pricePerSquare?: number;
  numberMode?: NumberMode;
  cellMode?: CellMode;   // "standard" | "paired" | "quad"
  cellShape?: CellShape; // "1x1" | "2x1" | "1x2" | "2x2"
  payoutConfig?: PayoutSchemeConfig;
  themeId?: string;
}

// Input for creating a template from an existing board
export interface CreateTemplateFromBoardInput {
  name: string;
  description?: string;
  includeAssignments?: boolean; // Include offline player assignments from board
}

// Input for updating a template
export interface UpdateTemplateInput {
  name?: string;
  description?: string;
  sport?: string;
  quarters?: number;
  pricePerSquare?: number;
  numberMode?: NumberMode;
  cellMode?: CellMode;     // "standard" | "paired" | "quad"
  cellShape?: CellShape;   // "1x1" | "2x1" | "1x2" | "2x2"
  payoutConfig?: PayoutSchemeConfig;
  themeId?: string | null; // null to remove theme
}

// Input for setting a square assignment in a template
// Exactly one of offlinePlayerId or playerConnectionId must be provided
export interface SquareAssignmentInput {
  offlinePlayerId?: string;  // Either this OR playerConnectionId
  playerConnectionId?: string;  // Either this OR offlinePlayerId
  row: number;
  col: number;
}

// Input for creating a board from a template
export interface CreateBoardFromTemplateInput {
  name: string;
  homeTeam: string;
  awayTeam: string;
  homeColor?: string;
  awayColor?: string;
  gameDate: string;
  slug?: string;
  description?: string;
  applyAssignments?: boolean; // Auto-assign squares based on template
  themeId?: string; // Optional theme to apply
}

// Result of validating template assignments before board creation
export interface TemplateValidationResult {
  valid: boolean;
  totalAssignments: number;
  validAssignments: number;
  missingPlayers?: MissingPlayerInfo[];
}

// Information about a template assignment that cannot be applied
export interface MissingPlayerInfo {
  offlinePlayerId?: string;
  playerConnectionId?: string;
  row: number;
  col: number;
  reason: string;
}

// =====================
// Credit Spend Types
// =====================

// Context for credit spend - what action triggered the spend
export type CreditSpendContext = 'create_board' | 'buy_theme' | 'upgrade_board' | 'other';

// Individual credit spend record
export interface CreditSpendRecord {
  id: string;
  creditsSpent: number;
  balanceAfter: number;   // Credit balance after this spend
  itemType: string;       // "board" | "theme" | etc.
  itemId: string;
  itemName: string;
  boardId?: string;       // Board ID if applicable
  context: CreditSpendContext;
  createdAt: string;
}

// Paginated response for credit spends
export interface CreditSpendListResponse {
  spends: CreditSpendRecord[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Cell Grouping Helper Functions
// ============================================================================

/**
 * Get the cell number (1-indexed) for a given square position.
 * Cell numbering starts at 1 and goes left-to-right, top-to-bottom.
 */
export function getCellForSquare(row: number, col: number, cellMode: CellMode, cellShape: CellShape): number {
  switch (cellMode) {
    case 'paired': {
      if (cellShape === '2x1') {
        // Paired Rows (vertical 2x1): 10 columns × 5 row-pairs = 50 cells
        const rowPair = Math.floor(row / 2);
        return rowPair * 10 + col + 1;
      }
      // Paired Columns (horizontal 1x2): 5 column-pairs × 10 rows = 50 cells
      const colPair = Math.floor(col / 2);
      return row * 5 + colPair + 1;
    }

    case 'quad': {
      // Quad 2x2: 5 column-pairs × 5 row-pairs = 25 cells
      const rowPair2x2 = Math.floor(row / 2);
      const colPair2x2 = Math.floor(col / 2);
      return rowPair2x2 * 5 + colPair2x2 + 1;
    }

    default: // 'standard'
      // Standard: each square is its own cell (1-100)
      return row * 10 + col + 1;
  }
}

/**
 * Get all (row, col) positions that belong to the given cell number.
 * Cell numbers are 1-indexed.
 */
export function getSquaresForCell(cellNumber: number, cellMode: CellMode, cellShape: CellShape): [number, number][] {
  // Convert to 0-indexed for calculations
  const cell = cellNumber - 1;

  switch (cellMode) {
    case 'paired': {
      if (cellShape === '2x1') {
        // Paired Rows (vertical 2x1): 10 columns × 5 row-pairs
        const rowPair = Math.floor(cell / 10);
        const col = cell % 10;
        const baseRow = rowPair * 2;
        return [[baseRow, col], [baseRow + 1, col]];
      }
      // Paired Columns (horizontal 1x2): 5 column-pairs × 10 rows
      const row = Math.floor(cell / 5);
      const colPair = cell % 5;
      const baseCol = colPair * 2;
      return [[row, baseCol], [row, baseCol + 1]];
    }

    case 'quad': {
      // Quad 2x2: 5 column-pairs × 5 row-pairs
      const rowPair2x2 = Math.floor(cell / 5);
      const colPair2x2 = cell % 5;
      const baseRow2x2 = rowPair2x2 * 2;
      const baseCol2x2 = colPair2x2 * 2;
      return [
        [baseRow2x2, baseCol2x2], [baseRow2x2, baseCol2x2 + 1],
        [baseRow2x2 + 1, baseCol2x2], [baseRow2x2 + 1, baseCol2x2 + 1],
      ];
    }

    default: {
      // Standard: each cell is one square
      const standardRow = Math.floor(cell / 10);
      const standardCol = cell % 10;
      return [[standardRow, standardCol]];
    }
  }
}

/**
 * Get the total number of cells for a given cell mode.
 */
export function getTotalCells(cellMode: CellMode): number {
  switch (cellMode) {
    case 'paired':
      return 50;
    case 'quad':
      return 25;
    default:
      return 100;
  }
}

/**
 * Get CSS style for cell boundary borders based on position and cell configuration.
 * Returns inset box-shadow style to show cell group borders.
 */
export function getCellBoundaryStyle(
  row: number,
  col: number,
  cellMode: CellMode,
  cellShape: CellShape,
  borderColor: string = 'rgba(0, 0, 0, 0.4)',
  borderWidth: number = 2
): React.CSSProperties {
  if (cellMode === 'standard') {
    return {};
  }

  let isTopEdge = false;
  let isBottomEdge = false;
  let isLeftEdge = false;
  let isRightEdge = false;

  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: every 2 rows form a cell
      isTopEdge = row % 2 === 0;
      isBottomEdge = row % 2 === 1;
      isLeftEdge = true;
      isRightEdge = true;
    } else {
      // Horizontal pairs (1x2): every 2 cols form a cell
      isTopEdge = true;
      isBottomEdge = true;
      isLeftEdge = col % 2 === 0;
      isRightEdge = col % 2 === 1;
    }
  } else if (cellMode === 'quad') {
    // 2x2 quads
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 2 === 0;
    isRightEdge = col % 2 === 1;
  }

  const shadows: string[] = [];
  if (isTopEdge) {
    shadows.push(`inset 0 ${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isBottomEdge) {
    shadows.push(`inset 0 -${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isLeftEdge) {
    shadows.push(`inset ${borderWidth}px 0 0 0 ${borderColor}`);
  }
  if (isRightEdge) {
    shadows.push(`inset -${borderWidth}px 0 0 0 ${borderColor}`);
  }

  if (shadows.length === 0) {
    return {};
  }

  return {
    boxShadow: shadows.join(', '),
  };
}

/**
 * Get CSS border-radius for a square based on its position within a cell group.
 * Inside corners are not rounded to make the cell appear connected.
 * Returns a borderRadius string like "4px 4px 0 0" (TL TR BR BL).
 */
export function getCellBorderRadius(
  row: number,
  col: number,
  cellMode: CellMode,
  cellShape: CellShape,
  radius: number = 4
): string {
  if (cellMode === 'standard') {
    return `${radius}px`;
  }

  // Determine which corners should be rounded
  // A corner is rounded only if it's on the outer edge of the cell group
  let topLeftRound = true;
  let topRightRound = true;
  let bottomRightRound = true;
  let bottomLeftRound = true;

  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: every 2 rows form a cell
      const isTop = row % 2 === 0;
      const isBottom = row % 2 === 1;
      // Top square: round top corners, flat bottom corners
      // Bottom square: flat top corners, round bottom corners
      if (isTop) {
        bottomLeftRound = false;
        bottomRightRound = false;
      }
      if (isBottom) {
        topLeftRound = false;
        topRightRound = false;
      }
    } else {
      // Horizontal pairs (1x2): every 2 cols form a cell
      const isLeft = col % 2 === 0;
      const isRight = col % 2 === 1;
      // Left square: round left corners, flat right corners
      // Right square: flat left corners, round right corners
      if (isLeft) {
        topRightRound = false;
        bottomRightRound = false;
      }
      if (isRight) {
        topLeftRound = false;
        bottomLeftRound = false;
      }
    }
  } else if (cellMode === 'quad') {
    // 2x2 quads
    const isTop = row % 2 === 0;
    const isBottom = row % 2 === 1;
    const isLeft = col % 2 === 0;
    const isRight = col % 2 === 1;

    // Top-left square: only top-left corner rounded
    // Top-right square: only top-right corner rounded
    // Bottom-left square: only bottom-left corner rounded
    // Bottom-right square: only bottom-right corner rounded
    topLeftRound = isTop && isLeft;
    topRightRound = isTop && isRight;
    bottomRightRound = isBottom && isRight;
    bottomLeftRound = isBottom && isLeft;
  }

  const tl = topLeftRound ? radius : 0;
  const tr = topRightRound ? radius : 0;
  const br = bottomRightRound ? radius : 0;
  const bl = bottomLeftRound ? radius : 0;

  return `${tl}px ${tr}px ${br}px ${bl}px`;
}

// =====================
// Enhanced Assign Mode Types
// =====================

// Player type in assignable players list
export type AssignablePlayerType =
  | 'board_participant'     // Online user already on board
  | 'board_offline_player'  // Offline player already on board
  | 'connection'            // Manager's connection not on board
  | 'offline_player'        // Manager's offline player not on board
  | 'pending_invite';       // Pending invitation

// Individual assignable player
export interface AssignablePlayer {
  type: AssignablePlayerType;
  // For online users
  userId?: string;
  user?: User;
  // For offline players
  offlinePlayerId?: string;
  offlinePlayer?: OfflinePlayer;
  // Board-specific info
  approvedSquares: number;
  claimedSquares: number;
  // For pending invites
  inviteEmail?: string;
  inviteId?: string;
  inviteExpiresAt?: string;
  // Status flags
  isOnBoard: boolean;
  isPending: boolean;
  hasReserved: boolean;
  reservedCount: number;
}

// Grouped result from assignable-players API
export interface AssignablePlayersResult {
  onBoard: AssignablePlayer[];        // Players already on the board
  connections: AssignablePlayer[];    // Online connections not on board
  offlinePlayers: AssignablePlayer[]; // Offline players not on board
  pendingInvites: AssignablePlayer[]; // Pending invitations
}

// Reserve squares input
export interface ReserveSquaresInput {
  userId: string;
  squareIds?: string[];  // Specific squares to reserve
  count?: number;        // Or reserve this many random squares
}

// Reserve squares result
export interface ReserveSquaresResult {
  squares: Square[];
  reservedCount: number;
}

// Unreserve squares input
export interface UnreserveSquaresInput {
  userId?: string;       // Unreserve all squares for this user
  squareIds?: string[];  // Or unreserve specific squares
}
